import React, { useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import moment from 'moment-mini';

import * as UIAction from '../../redux/actions/ui.actions';
import * as SourceCardUtil from '../availableSources/SourceCardUtil';
import {
  Card as MuiCard,
  CardActionArea,
  Typography,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';

import { WorkOutline, WarningAmberOutlined, Check} from '@mui/icons-material';

import { styled, unstable_extendSxProp as sx } from '@mui/system';
import { fetchJob } from '../../redux/actions';
import { getLogoUrl } from '@hotglue/common';
import ActiveGrayed from '../../assets/main-logo-active.svg';
import { getTenantId, isJobPending } from '../../redux/selectors';
const Card = styled(MuiCard)(({ theme, ...props }) =>
  sx({
    '&': {
      border: `1px solid ${theme.palette.common.outline}`,
      borderRadius: `8px`,
      '&:hover': {
        color: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.primary.main}`,
      },
    },
    '& .container': {
      display: 'flex',
      gap: theme.spacing(4),
      flexDirection: 'column',
      justifyContent: 'space-between',
      minHeight: '170px',
      padding: '24px 16px',
    },
  }),
);

const FlowCard = ({
  id,
  name,
  description,
  flowIcon,
  linked,
  type,
  version,
  fetchJob,
  onClick,
}) => {
  const lastJob = useSelector((state) => state.jobs.lastJob[`${getTenantId()}/${id}`]);
  const pendingJob = useSelector((state) => isJobPending(state, lastJob?.s3_root, id));
  const sources = useSelector((state) => state.sources.supported[id]);
  const _targets = useSelector((state) => state.targets.supported[id]);
  const connectors = useSelector((state) => state.connectors.supported[id]);
  const hideJobStatusBadge = useSelector((state) => state.configuration.hideJobStatusBadge);
  const [targetsLength, setTargetsLength] = useState(undefined);
  const [tapsLength, setTapsLength] = useState(undefined);

  const arrayOfTargets = useMemo(() => {
    const targetsInArray = _targets && Object.values(_targets);
    if (targetsInArray?.length > 4) {
      setTargetsLength(targetsInArray.slice(4).length);
      return targetsInArray.slice(0, 4);
    }

    return _targets && Object.values(_targets);
  }, [_targets]);

  const arrayOfSource = useMemo(() => {
    const sourcesInArray = sources && Object.values(sources);
    if (sourcesInArray?.length > 4) {
      setTapsLength(sourcesInArray.slice(4).length);
      return sourcesInArray.slice(0, 4);
    }

    return sources && Object.values(sources);
  }, [sources]);

  const arrayOfConnectors = useMemo(() => {
    const connectorsInArray = connectors && Object.values(connectors);

    if (connectorsInArray?.length > 4) {
      setTapsLength(connectorsInArray.slice(4).length);
      return connectorsInArray.slice(0, 4);
    }

    return connectors && Object.values(connectors);
  }, [connectors]);

  React.useEffect(() => {
    if (linked && (!lastJob || lastJob.flow_id !== id)) {
      // If there is no last job, check if there is one
      fetchJob(id);
    }
  }, [lastJob]);

  React.useEffect(() => {
    if (
      linked &&
      !pendingJob &&
      lastJob &&
      lastJob.status &&
      !(lastJob.status.includes('FAILED') || lastJob.status.includes('COMPLETE'))
    ) {
      console.log(`Polling job ${lastJob.job_id}`);
      SourceCardUtil.pollJob(lastJob.s3_root, id, getTenantId());
    }
  }, [lastJob]);

  return (
    <Card elevation={0} background="#ffc000">
      <CardActionArea
        id={linked ? 'LinkedFlowCard' : 'FlowCard'}
        className="container"
        onClick={onClick || (() => UIAction.clickFlow(id))}
        sx={{
          p: 3,
        }}
      >
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box display="flex" gap={2}>
            <Box
              component="img"
              src={flowIcon || ActiveGrayed}
              role='img'
              alt={`Flow ${name} icon`}
              className="img"
              sx={{ objectFit: 'cover' }}
              onError={(e) => {
                e.target.src = ActiveGrayed;
              }}
              width={32}
              height={32}
            />

            <Box display="flex" flexDirection="column" width="100%" gap={0.5}>
              <Typography variant="h2">{name}</Typography>
              <Typography variant="body1" fontWeight="300" color="text.lightish">
                {description}
              </Typography>
            </Box>
          </Box>
          {linked && (
            <Box
              display="flex"
              py={'2px'}
              alignItems="center"
              px={'8px'}
              gap={0.625}
              role='tooltip'
              aria-label='Connected tooltip'
              borderRadius={4}
              sx={{
                bgcolor: ({ palette: { success } }) => success.main,
              }}
              height="fit-content"
            >
              <Check sx={{ fontSize: '12px', color: 'common.white' }} />
              <Typography variant="caption" sx={{ fontSize: '8px' }} fontWeight={700} color="common.white">
                CONNECTED
              </Typography>
            </Box>
          )}
        </Box>
        <Box display="flex" flexDirection="row" justifyContent="space-between" width={1}>
          <Box display="flex" alignItems="end" minWidth="29px">
            {pendingJob && !hideJobStatusBadge ? (
              <Tooltip title={`Job Running`} placement="top">
                <Box>
                  <Box
                    role='tooltip'
                    aria-label='job running tooltip'
                    width="28px"
                    height="28px"
                    sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  >
                    <WorkOutline sx={{ fontSize: '13px', position: 'absolute' }} />

                    <CircularProgress
                      size={24}
                      sx={{
                        color: '#FA5240',
                        position: 'absolute',
                        zIndex: 1,
                      }}
                    />
                  </Box>
                </Box>
              </Tooltip>
            ) : lastJob && !hideJobStatusBadge ? (
              lastJob.status === 'JOB_COMPLETED' ? (
                <Tooltip
                  title={`Last synced at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                  placement="top"
                >
                  <Box
                    border="1px solid #36C53C"
                    width={28}
                    role='tooltip'
                    aria-label={`Last synced at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                    height={28}
                    borderRadius="32px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <WorkOutline sx={{ fontSize: '16px' }} />
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip
                  title={`Sync failed at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                  placement="top"
                >
                  <Box
                    border="1px solid #FA2A52"
                    width={28}
                    role='tooltip'
                    aria-label={`Sync failed at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                    height={28}
                    borderRadius="32px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <WarningAmberOutlined sx={{ fontSize: '16px', color: '#FA2A52' }} />
                  </Box>
                </Tooltip>
              )
            ) : (
              ``
            )}
          </Box>
          <Box>
            {version === 2
              ? Array.isArray(arrayOfConnectors) &&
                arrayOfConnectors.length > 0 && (
                  <EntityIcons flowName={name} entities={arrayOfConnectors} entityLength={targetsLength} />
                )
              : type
              ? Array.isArray(arrayOfTargets) &&
                arrayOfTargets.length > 0 && (
                  <EntityIcons flowName={name} entities={arrayOfTargets} entityLength={targetsLength} isTarget={true} />
                )
              : Array.isArray(arrayOfSource) &&
                arrayOfSource.length > 0 && (
                  <EntityIcons flowName={name} entities={arrayOfSource} entityLength={tapsLength} />
                )}
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
};

const EntityIcons = (props) => {
  const { entities, entityLength, flowName } = props;
  return (
    <Box display="flex" justifyContent="end" alignItems="end" gap={1} width="100%" minHeight={28}>
      {entities &&
        entities.length > 0 &&
        entities.map((entity) => {
          const icon = getLogoUrl(entity, 24);
          return (
            <Box
              key={`${flowName}-${entity.label}-v${entity.version ?? 'v1'}`}
              width={28}
              height={28}
              borderRadius="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                border: (theme) => `1px solid ${theme.palette.common.outline}`,
              }}
            >
              <Box component="img" role='img' width={24} height={24} src={icon} alt={`${entity.label} icon`} sx={{ objectFit: 'contain' }} />
            </Box>
          );
        })}
      {entityLength && (
        <Box
          width={28}
          height={28}
          borderRadius="100%"
          bgcolor="accent.main"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="body1" fontWeight={500} color="common.white">
            {entityLength}+
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const mapStateToProps = (state, props) => {


  return {

  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchJob: (flowId) => dispatch(fetchJob(flowId)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FlowCard);
